import { useClient } from '@cortex/react-query';
import type { CortexClient } from '@cortex/sdk';
import type {
  MCPConnection,
  McpConnectionNameInUseError,
  McpConnectionRenamePropagation,
} from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';

import { useAuth } from '../auth/AuthProvider';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { Modal } from '../components/common/Modal';
import { PageHeader } from '../components/common/PageHeader';
import { useToast } from '../components/common/Toast';
import { InspectorDrawer } from '../components/mcp-connections/InspectorDrawer';
import type { ITestConnectionResult } from '../components/mcp-connections/McpToolsEditor';
import {
  ReferentList,
  deleteWarningsSummary,
  propagatedSummary,
  readConnectionInUseBody,
  readDeleteWarnings,
} from '../components/mcp-connections/rename-propagation';
import {
  fullBleedTemplateIcons,
  templateBrandIcons,
} from '../components/mcp-connections/template-icons';
import { isUserTokenCat, userTokenAuthErrorsByCategory } from '../lib/mcp-connection-health';

export const Route = createFileRoute('/mcp-connections')({ component: MCPConnectionsPage });

interface IPageEnvelope<T> {
  items: T[];
  total: number;
}

const stack6: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-6)',
};
const stack3: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
};
const stack2: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};
const grid2: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 'var(--sp-3)',
};
const rowH: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
};
const rowHWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-3)',
  flexWrap: 'wrap',
};

function listKey() {
  return ['mcp-connections'] as const;
}

/**
 * True for cluster-internal endpoints (localhost, or a bare service name with
 * no dotted FQDN like `http://mcp-server:3005/mcp`). These are a
 * platform-managed implementation detail — the console shows "Managed by
 * Formant" rather than surfacing an internal address the user can't reach.
 */
function isInternalEndpoint(endpoint: string): boolean {
  try {
    const { hostname } = new URL(endpoint);
    return hostname === 'localhost' || hostname === '127.0.0.1' || !hostname.includes('.');
  } catch {
    return false;
  }
}

/** Strip secrets and internal storage paths from the read-only JSON panel. */
function authConfigForDisplay(cfg: Record<string, unknown> | null): Record<string, unknown> | null {
  if (!cfg || typeof cfg !== 'object') return cfg;
  const copy = { ...cfg };
  delete copy.clientSecret;
  delete copy.clientSecretVaultPath;
  delete copy.registrationAccessToken;
  return copy;
}

/**
 * Templates for popular hosted MCP servers.
 *   - `dcr`: one-click, multi-selectable. The server supports OAuth discovery +
 *     dynamic client registration, so we create the row with an empty
 *     `authConfig` and Connect runs the "Auto" flow — no credentials to paste.
 *   - `guided`: opens the custom form prefilled + a `note` with setup steps,
 *     because the provider needs a pre-registered client id/secret (Slack), a
 *     static token (PagerDuty), or a per-account endpoint (Datadog, Snowflake).
 * Monogram icons stand in for brand SVGs for now.
 */
interface IMcpTemplate {
  key: string;
  name: string;
  description: string;
  mode: 'dcr' | 'guided';
  /** Prefilled endpoint; '' when the user must supply a per-account URL. */
  endpoint: string;
  transport: 'http' | 'sse';
  authType: 'oauth' | 'api_key';
  color: string;
  mono: string;
  /** guided only: setup instructions shown at the top of the form. */
  note?: string;
}

const mcpTemplates: IMcpTemplate[] = [
  // ── One-click (OAuth + dynamic client registration) ──────────────────────
  {
    key: 'atlassian',
    name: 'Atlassian',
    description: 'Jira & Confluence',
    mode: 'dcr',
    endpoint: 'https://mcp.atlassian.com/v1/sse',
    transport: 'sse',
    authType: 'oauth',
    color: '#0C66E4',
    mono: 'A',
  },
  {
    key: 'github',
    name: 'GitHub',
    description: 'Repos, issues, PRs',
    mode: 'dcr',
    endpoint: 'https://api.githubcopilot.com/mcp/',
    transport: 'http',
    authType: 'oauth',
    color: '#1F2328',
    mono: 'GH',
  },
  {
    key: 'sentry',
    name: 'Sentry',
    description: 'Errors & performance',
    mode: 'dcr',
    endpoint: 'https://mcp.sentry.dev/mcp',
    transport: 'http',
    authType: 'oauth',
    color: '#362D59',
    mono: 'S',
  },
  {
    key: 'linear',
    name: 'Linear',
    description: 'Issues & projects',
    mode: 'dcr',
    endpoint: 'https://mcp.linear.app/sse',
    transport: 'sse',
    authType: 'oauth',
    color: '#5E6AD2',
    mono: 'L',
  },
  {
    key: 'notion',
    name: 'Notion',
    description: 'Docs & databases',
    mode: 'dcr',
    endpoint: 'https://mcp.notion.com/mcp',
    transport: 'http',
    authType: 'oauth',
    color: '#0F0F0F',
    mono: 'N',
  },
  {
    key: 'supermemory',
    name: 'Supermemory',
    description: 'Personal memory graph',
    mode: 'dcr',
    endpoint: 'https://mcp.supermemory.ai/mcp',
    transport: 'http',
    authType: 'oauth',
    color: '#1E293B',
    mono: 'SM',
  },
  // ── Guided setup (need a client id / token / per-account URL) ─────────────
  {
    key: 'slack',
    name: 'Slack',
    description: 'Channels, messages, search',
    mode: 'guided',
    endpoint: 'https://mcp.slack.com/mcp',
    transport: 'http',
    authType: 'oauth',
    color: '#4A154B',
    mono: '#',
    note:
      'Slack does not support automatic client registration. Create a Slack app at ' +
      'https://api.slack.com/apps, and under "OAuth & Permissions" add your Cortex OAuth ' +
      "callback URL as a Redirect URL. Then paste the app's Client ID and Client Secret below.",
  },
  {
    key: 'pagerduty',
    name: 'PagerDuty',
    description: 'Incidents, services, on-calls',
    mode: 'guided',
    endpoint: 'https://mcp.pagerduty.com/mcp',
    transport: 'http',
    authType: 'api_key',
    color: '#06AC38',
    mono: 'PD',
    note:
      'Create a User API Token in PagerDuty (User Settings → API Access), then paste it below ' +
      'as the Authorization header value: "Bearer <token>". EU accounts use ' +
      'https://mcp.eu.pagerduty.com/mcp.',
  },
  {
    key: 'zoom',
    name: 'Zoom',
    description: 'Meetings & recordings',
    mode: 'guided',
    endpoint: 'https://mcp.zoom.us/mcp/zoom/streamable',
    transport: 'http',
    authType: 'oauth',
    color: '#0B5CFF',
    mono: 'Z',
    note:
      'Zoom uses OAuth. If Connect reports a missing client id, create a Zoom OAuth app in the ' +
      'Zoom Marketplace and paste its Client ID / Secret below.',
  },
  {
    key: 'datadog',
    name: 'Datadog',
    description: 'Metrics, logs, monitors',
    mode: 'guided',
    endpoint: '',
    transport: 'http',
    authType: 'oauth',
    color: '#632CA6',
    mono: 'DD',
    note:
      "Datadog's MCP URL is specific to your Datadog site — get it from " +
      'https://docs.datadoghq.com/mcp_server/ and paste it as the Endpoint URL. Auth is OAuth.',
  },
  {
    key: 'snowflake',
    name: 'Snowflake',
    description: 'Warehouse-managed MCP',
    mode: 'guided',
    endpoint: '',
    transport: 'http',
    authType: 'oauth',
    color: '#29B5E8',
    mono: 'SF',
    note:
      'Snowflake exposes a warehouse-managed MCP endpoint that is specific to your account — ' +
      "paste your account's MCP URL as the Endpoint URL. Auth is OAuth.",
  },
  {
    key: 'fal',
    name: 'fal',
    description: 'Generative media models',
    mode: 'guided',
    endpoint: 'https://mcp.fal.ai/mcp',
    transport: 'http',
    authType: 'api_key',
    color: '#0F0F0F',
    mono: 'fal',
    note:
      'Authenticate with your fal API key (FAL_KEY) as the Authorization header value: ' +
      '"Key <FAL_KEY>" — create one in the fal dashboard. See ' +
      'https://fal.ai/docs/documentation/setting-up/mcp.',
  },
];

type CreateFormInitial = {
  name?: string;
  endpoint?: string;
  transport?: 'http' | 'sse';
  authMode?: 'none' | 'token' | 'oauth';
  note?: string;
};

/** A selectable provider tile: brand-colored monogram + name + one-liner. */
function TemplateTile({
  icon,
  iconFullBleed,
  mono,
  color,
  name,
  desc,
  selected,
  cornerCheck,
  onClick,
}: {
  /** Brand SVG; when absent, a brand-colored monogram chip is shown instead. */
  icon?: ReactNode;
  /** Icon carries its own background square → fill the chip instead of a white pad. */
  iconFullBleed?: boolean;
  mono: string;
  color: string;
  name: string;
  desc: string;
  selected?: boolean;
  cornerCheck?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'flex',
        gap: 'var(--sp-2)',
        alignItems: 'center',
        textAlign: 'left',
        padding: 'var(--sp-3)',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        border: `1px solid ${selected ? 'var(--border-accent)' : 'var(--border-subtle)'}`,
        background: selected ? 'var(--surface-active)' : 'var(--surface-2)',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: 8,
          overflow: 'hidden',
          background: icon ? (iconFullBleed ? 'transparent' : '#fff') : color,
          color: '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        {icon ?? mono}
      </span>
      <span style={{ minWidth: 0 }}>
        <span
          style={{ display: 'block', fontWeight: 'var(--fw-med)', color: 'var(--text-primary)' }}
        >
          {name}
        </span>
        <span
          className="t-xs t-muted"
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {desc}
        </span>
      </span>
      {cornerCheck && selected ? (
        <span style={{ position: 'absolute', top: 6, right: 8, color: 'var(--horizon-glow)' }}>
          ✓
        </span>
      ) : null}
    </button>
  );
}

/**
 * Modal body for adding connections: a template gallery (multi-select the
 * one-click OAuth providers → "Add N"), plus Slack + Custom tiles that open the
 * manual form (Slack prefilled).
 */
function AddConnectionContent({
  client,
  onCreated,
}: {
  client: CortexClient;
  onCreated: () => void;
}) {
  const toast = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [custom, setCustom] = useState<CreateFormInitial | null>(null);

  const oneClick = mcpTemplates.filter((t) => t.mode === 'dcr');
  const guided = mcpTemplates.filter((t) => t.mode === 'guided');

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Open the manual form prefilled from a guided template. */
  const openGuided = (t: IMcpTemplate) =>
    setCustom({
      name: t.name,
      endpoint: t.endpoint,
      transport: t.transport,
      authMode: t.authType === 'oauth' ? 'oauth' : 'token',
      note: t.note,
    });

  const add = useMutation({
    mutationFn: async () => {
      const chosen = oneClick.filter((t) => selected.has(t.key));
      for (const t of chosen) {
        await client.http
          .post('v1/mcp-connections', {
            json: {
              name: t.name,
              endpoint: t.endpoint,
              transport: t.transport,
              authType: t.authType,
              ...(t.authType === 'oauth' ? { authConfig: {} } : {}),
            },
          })
          .json();
      }
      return chosen.length;
    },
    onSuccess: (n) => {
      toast.show({
        kind: 'success',
        text: `Added ${n} connection${n === 1 ? '' : 's'} — click Connect on each to authorize.`,
      });
      onCreated();
    },
    onError: (e) => toast.show({ kind: 'error', text: (e as Error).message }),
  });

  if (custom) {
    return (
      <CreateForm
        client={client}
        onCreated={onCreated}
        initial={custom}
        onBack={() => setCustom(null)}
      />
    );
  }

  const tileGrid: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
    gap: 'var(--sp-2)',
  };

  return (
    <div style={stack6}>
      <div style={stack2}>
        <span className="t-mono-label">ONE-CLICK</span>
        <div style={tileGrid}>
          {oneClick.map((t) => (
            <TemplateTile
              key={t.key}
              icon={templateBrandIcons[t.key]}
              iconFullBleed={fullBleedTemplateIcons.has(t.key)}
              mono={t.mono}
              color={t.color}
              name={t.name}
              desc={t.description}
              selected={selected.has(t.key)}
              cornerCheck={true}
              onClick={() => toggle(t.key)}
            />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={selected.size === 0 || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending
              ? 'Adding…'
              : selected.size
                ? `Add ${selected.size} connection${selected.size === 1 ? '' : 's'}`
                : 'Add connections'}
          </button>
        </div>
      </div>

      <div style={stack2}>
        <span className="t-mono-label">GUIDED SETUP</span>
        <p className="t-xs t-muted" style={{ margin: 0 }}>
          These need a client id/secret, a token, or your account-specific URL — we&rsquo;ll walk
          you through it.
        </p>
        <div style={tileGrid}>
          {guided.map((t) => (
            <TemplateTile
              key={t.key}
              icon={templateBrandIcons[t.key]}
              iconFullBleed={fullBleedTemplateIcons.has(t.key)}
              mono={t.mono}
              color={t.color}
              name={t.name}
              desc={t.description}
              onClick={() => openGuided(t)}
            />
          ))}
          <TemplateTile
            mono="+"
            color="var(--surface-raised)"
            name="Custom"
            desc="Any MCP endpoint"
            onClick={() => setCustom({})}
          />
        </div>
      </div>
    </div>
  );
}

function MCPConnectionsPage() {
  const client = useClient();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: listKey(),
    queryFn: () => client.http.get('v1/mcp-connections').json<IPageEnvelope<MCPConnection>>(),
  });

  const refetch = () => qc.invalidateQueries({ queryKey: listKey() });

  const allRows = list.data?.items ?? [];
  // A user-token built-in (Formant Robotics / Metaphysics) is "connected" for
  // the org when an org-owned row of the same category holds a keyring.
  const connectedUserTokenCats = new Set(
    allRows
      .filter((r) => r.orgId !== null && isUserTokenCat(r.category) && r.hasKeyring)
      .map((r) => r.category as string),
  );
  // Re-auth markers live on the org-owned rows too — surface them on the
  // visible catalog built-in so a dead refresh chain shows "Reconnect
  // required" instead of a healthy Connected state.
  const userTokenAuthErrors = userTokenAuthErrorsByCategory(allRows);
  // Hide the org-owned user-token rows: they are the connection STATE of the
  // built-in catalog row and are managed via its Connect / Disconnect action.
  const isFormantBuiltIn = (r: MCPConnection) => r.orgId === null && isUserTokenCat(r.category);
  const visibleRows = allRows.filter((r) => !(r.orgId !== null && isUserTokenCat(r.category)));

  // Group into Formant (the built-ins, Metaphysics then Robotics) and External.
  const formantOrder: Record<string, number> = { formant_metaphysics: 0, formant_telemetry: 1 };
  const formantRows = visibleRows
    .filter(isFormantBuiltIn)
    .sort((a, b) => (formantOrder[a.category ?? ''] ?? 9) - (formantOrder[b.category ?? ''] ?? 9));
  const externalRows = visibleRows.filter((r) => !isFormantBuiltIn(r));

  const renderRow = (row: MCPConnection, i: number) => (
    <li
      key={row.id}
      style={{
        padding: 'var(--sp-3) 0',
        borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)',
      }}
    >
      <ConnectionRow
        row={row}
        client={client}
        onChange={refetch}
        connectedUserToken={connectedUserTokenCats.has(row.category ?? '')}
        authError={
          isFormantBuiltIn(row)
            ? (userTokenAuthErrors.get(row.category ?? '') ?? null)
            : (row.authError ?? null)
        }
      />
    </li>
  );

  const newButton = (
    <button type="button" className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>
      New MCP connection
    </button>
  );

  return (
    <div style={stack6}>
      <PageHeader title="Connections" />

      {list.isLoading ? (
        <p className="t-sm t-secondary">Loading…</p>
      ) : list.error ? (
        <p className="t-sm" style={{ color: 'var(--status-danger)' }}>
          Failed to load: {(list.error as Error).message}
        </p>
      ) : (
        <>
          <div className="card">
            <div className="card-header">
              <span className="t-mono-label">CONNECTIONS · FORMANT</span>
            </div>
            <div className="card-body">
              {formantRows.length === 0 ? (
                <p className="t-sm t-secondary">No Formant MCPs available.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {formantRows.map(renderRow)}
                </ul>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header is-row">
              <span className="t-mono-label">CONNECTIONS · EXTERNAL</span>
              {newButton}
            </div>
            <div className="card-body">
              {externalRows.length === 0 ? (
                <p className="t-sm t-secondary">
                  No external connections yet — use “New MCP connection” to add one.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {externalRows.map(renderRow)}
                </ul>
              )}
            </div>
          </div>
        </>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        eyebrow="CONNECTIONS · NEW"
        title="Add MCP connection"
        width={640}
      >
        <AddConnectionContent
          client={client}
          onCreated={() => {
            setCreating(false);
            refetch().catch(() => {});
          }}
        />
      </Modal>
    </div>
  );
}

/** Vertical ⋮ menu — edit + delete for org-owned rows. */
function ConnectionOverflowMenu({
  onEdit,
  onDelete,
  removePending,
}: {
  onEdit?: () => void;
  onDelete: () => void;
  removePending: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div style={{ position: 'relative' }} ref={rootRef}>
      <button
        type="button"
        className="btn btn--outline btn--icon-sm"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More connection actions"
        onClick={() => setOpen((v) => !v)}
      >
        <span style={{ userSelect: 'none', fontSize: 18, lineHeight: 1 }} aria-hidden={true}>
          ⋮
        </span>
      </button>
      {open ? (
        <div
          role="menu"
          className="popover-mock"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 4,
            minWidth: '11rem',
            zIndex: 20,
          }}
        >
          {onEdit ? (
            <button
              type="button"
              role="menuitem"
              className="menu-item"
              style={{
                width: '100%',
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
              }}
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            >
              Edit MCP connection
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="menu-item is-destructive"
            style={{ width: '100%', border: 'none', background: 'transparent', textAlign: 'left' }}
            disabled={removePending}
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            {removePending ? 'Removing…' : 'Delete connection'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function ConnectionRow({
  row,
  client,
  onChange,
  connectedUserToken = false,
  authError = null,
}: {
  row: MCPConnection;
  client: CortexClient;
  onChange: () => void;
  /** For a user-token built-in: whether the org has already connected it. */
  connectedUserToken?: boolean;
  /**
   * Definitive refresh failure — the stored refresh token was rejected and only
   * a reconnect restores access. Resolved by the caller, which knows whether
   * this row is a catalog built-in (marker lives on the hidden org-owned row of
   * the same category) or an ordinary org-owned row (carries it itself).
   */
  authError?: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [inspecting, setInspecting] = useState(false);
  const [managingCreds, setManagingCreds] = useState(false);
  const [confirmUserToken, setConfirmUserToken] = useState(false);
  const toast = useToast();
  const { getConnectTokens } = useAuth();

  /** Built-in Formant MCP that connects with the logged-in user's own tokens. */
  const isUserTokenBuiltIn = row.orgId === null && isUserTokenCat(row.category);

  const connect = useMutation({
    mutationFn: () =>
      client.http.post(`v1/mcp-connections/${row.id}/connect`).json<{ userActionUrl: string }>(),
    onSuccess: ({ userActionUrl }) => openOAuthPopup(userActionUrl, onChange),
  });

  // User-token connect: forward the caller's own access (+ refresh) tokens.
  const connectUserToken = useMutation({
    mutationFn: async () => {
      const tokens = await getConnectTokens();
      if (!tokens?.accessToken) {
        throw new Error('No active session — sign in again, then retry.');
      }
      return client.http
        .post(`v1/mcp-connections/${row.id}/user-token`, {
          json: { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken ?? undefined },
        })
        .json<{ userActionUrl?: string }>();
    },
    onSuccess: (res) => {
      setConfirmUserToken(false);
      // Keycloak login + Robotics: the backend can't use the Keycloak token
      // against the Robotics MCP, so it returns an OAuth authorize URL — open
      // it so the user signs in to Formant; the popup completion refetches.
      if (res?.userActionUrl) {
        openOAuthPopup(res.userActionUrl, onChange);
        return;
      }
      toast.show({ kind: 'success', text: `Connected ${row.name} with your Formant login.` });
      onChange();
    },
    onError: (e) => toast.show({ kind: 'error', text: (e as Error).message }),
  });
  const disconnectUserToken = useMutation({
    mutationFn: () => client.http.delete(`v1/mcp-connections/${row.id}/user-token`),
    onSuccess: () => onChange(),
  });
  const test = useMutation({
    mutationFn: () =>
      client.http
        .post(`v1/mcp-connections/${row.id}/test-connection`)
        .json<ITestConnectionResult>(),
    // A successful probe rewrites `toolSchemaCache` server-side. Without this
    // refetch every cached connection list keeps serving the pre-probe row
    // (`staleTime: 30_000`), so tool pickers elsewhere — the `mcp-query` node
    // form, the watch wizard — still read an empty cache right after the user
    // watched this test succeed (DEV-17129).
    onSuccess: onChange,
  });
  const disconnect = useMutation({
    mutationFn: () => client.http.delete(`v1/mcp-connections/${row.id}/keyring`),
    onSuccess: () => {
      // Clear last Test result — otherwise "Connected — …" survives disconnect.
      test.reset();
      onChange();
    },
  });
  const remove = useMutation({
    mutationFn: async () => {
      const res = await client.http.delete(`v1/mcp-connections/${row.id}`);
      // The DELETE answers 200 + `{ warnings }` naming referents that will no
      // longer resolve; older API builds answered an empty 204 — tolerate it.
      // (Only the body parse is guarded — HTTP errors still reject above.)
      return res.json<unknown>().catch(() => null);
    },
    onSuccess: (body) => {
      const summary = deleteWarningsSummary(readDeleteWarnings(body));
      if (summary) toast.show({ kind: 'info', text: summary, timeoutMs: 8_000 });
      onChange();
    },
  });

  /** Org-owned, non-OAuth rows can carry a static bearer token / API key.
   *  User-token built-ins connect via the dedicated flow, not manual creds. */
  const canManageCredentials = row.authType !== 'oauth' && !isUserTokenBuiltIn;
  const showOAuthWarning = row.authType === 'oauth' && !row.hasKeyring && !connect.isPending;
  /** No-credentials servers can always be probed; OAuth needs a keyring first.
   *  A user-token built-in is testable once connected — the spec calls resolve
   *  its bearer by category (via the org-owned row's keyring). */
  const canTest =
    row.authType === 'none' || row.hasKeyring || (isUserTokenBuiltIn && connectedUserToken);
  /** Catalog rows (`orgId` null) are read-only server-side — no delete / edit affordance. */
  const canDelete = row.orgId !== null;
  const canEdit = row.orgId !== null;
  const isBuiltIn = row.orgId === null;

  return (
    <div style={stack3}>
      <div style={{ ...rowH, gap: 'var(--sp-4)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...rowH, gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <span
              style={{
                fontWeight: 'var(--fw-med)',
                color: 'var(--text-primary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {row.name}
            </span>
            {authError ? (
              <span
                className="badge badge--warning"
                title={`Token refresh was rejected — disconnect and reconnect to restore access. (${authError})`}
              >
                Reconnect required
              </span>
            ) : null}
            {/* Formant built-ins live under the FORMANT group and are
                platform-managed — the Built-in / transport / auth badges are
                all noise there. Other rows keep them. */}
            {isUserTokenBuiltIn ? null : (
              <>
                {isBuiltIn ? (
                  <span
                    className="badge badge--tool"
                    title="Catalog MCP connection managed by Formant — your org connects with its own credentials."
                  >
                    Built-in
                  </span>
                ) : null}
                <span className="badge badge--neutral">{row.transport}</span>
                <span className="badge badge--neutral">{authLabel(row)}</span>
              </>
            )}
          </div>
          {row.description ? (
            <p className="t-xs t-muted" style={{ marginTop: 2, maxWidth: '62ch' }}>
              {row.description}
            </p>
          ) : null}
          {/* Endpoint URL — hidden for the Formant built-ins (platform-managed,
              internal/opaque address) and any cluster-internal endpoint. */}
          {isUserTokenBuiltIn || isInternalEndpoint(row.endpoint) ? null : (
            <p
              className="t-xs t-muted"
              style={{
                marginTop: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '62ch',
              }}
            >
              {row.endpoint}
            </p>
          )}
          {row.authType === 'oauth' && row.authConfig && Object.keys(row.authConfig).length > 0 ? (
            <details style={{ marginTop: 'var(--sp-2)', fontSize: 'var(--fs-xs)' }}>
              <summary
                style={{
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                }}
              >
                OAuth config (read-only)
              </summary>
              <pre
                className="t-mono"
                style={{
                  maxHeight: '12rem',
                  overflow: 'auto',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface-2)',
                  padding: 'var(--sp-2)',
                  fontSize: 11,
                  lineHeight: 1.4,
                  marginTop: 4,
                }}
              >
                {JSON.stringify(authConfigForDisplay(row.authConfig), null, 2)}
              </pre>
            </details>
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 'var(--sp-2)',
            flexShrink: 0,
          }}
        >
          {canTest ? (
            <button
              type="button"
              className="btn btn--outline btn--sm"
              onClick={() => setInspecting(true)}
              title="Inspect tools, prompts and resources — each tool also has an Annotate action here"
            >
              Test
            </button>
          ) : null}

          {isUserTokenBuiltIn ? (
            connectedUserToken ? (
              <button
                type="button"
                className="btn btn--outline btn--sm"
                onClick={() => {
                  if (
                    confirm(
                      'Disconnect this MCP? Org workflows using it will fail until reconnected.',
                    )
                  ) {
                    disconnectUserToken.mutate();
                  }
                }}
                disabled={disconnectUserToken.isPending}
              >
                Disconnect
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => setConfirmUserToken(true)}
                disabled={connectUserToken.isPending}
              >
                {connectUserToken.isPending ? 'Connecting…' : 'Connect'}
              </button>
            )
          ) : null}

          {row.authType === 'oauth' ? (
            row.hasKeyring ? (
              <button
                type="button"
                className="btn btn--outline btn--sm"
                onClick={() => {
                  if (
                    confirm('Disconnect this MCP? Workflows using it will fail until reconnected.')
                  ) {
                    disconnect.mutate();
                  }
                }}
                disabled={disconnect.isPending}
              >
                Disconnect
              </button>
            ) : (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  if (confirmOrgWideWarning()) connect.mutate();
                }}
                disabled={connect.isPending}
              >
                {connect.isPending ? 'Opening…' : 'Connect'}
              </button>
            )
          ) : null}

          {canManageCredentials ? (
            <button
              type="button"
              className={row.hasKeyring ? 'btn btn--outline btn--sm' : 'btn btn--primary btn--sm'}
              onClick={() => setManagingCreds((v) => !v)}
            >
              {row.hasKeyring ? 'Edit credentials' : 'Add credentials'}
            </button>
          ) : null}

          {canDelete ? (
            <ConnectionOverflowMenu
              onEdit={canEdit ? () => setEditing(true) : undefined}
              removePending={remove.isPending}
              onDelete={() => {
                if (
                  confirm(
                    'Remove this MCP connection from your organization?\n\n' +
                      'Workflows that reference it will break until you add it again.',
                  )
                ) {
                  remove.mutate();
                }
              }}
            />
          ) : null}
        </div>
        {showOAuthWarning && connect.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(connect.error as Error).message}
          </span>
        ) : null}
        {canDelete && remove.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(remove.error as Error).message}
          </span>
        ) : null}
      </div>

      {canTest ? (
        <TestResultPanel result={test.data} pending={test.isPending} error={test.error} />
      ) : null}

      {managingCreds && canManageCredentials ? (
        <CredentialsEditor
          key={`${row.id}-creds`}
          row={row}
          client={client}
          onCancel={() => setManagingCreds(false)}
          onSaved={() => {
            setManagingCreds(false);
            test.reset();
            onChange();
          }}
        />
      ) : null}
      {editing && canEdit ? (
        <EditConnectionForm
          key={row.id}
          row={row}
          client={client}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onChange();
          }}
        />
      ) : null}
      {inspecting ? (
        <InspectorDrawer client={client} connection={row} onClose={() => setInspecting(false)} />
      ) : null}

      {isUserTokenBuiltIn ? (
        <ConfirmDialog
          open={confirmUserToken}
          onClose={() => setConfirmUserToken(false)}
          onConfirm={() => connectUserToken.mutate()}
          eyebrow="ORG-WIDE CREDENTIAL"
          title={`Connect to ${row.name} MCP?`}
          confirmLabel={connectUserToken.isPending ? 'Connecting…' : 'Connect'}
          pending={connectUserToken.isPending}
          message={
            <p>
              This connection is <strong>organization-wide</strong>: all usage of this MCP by anyone
              in the org runs as <strong>you</strong>, using your Formant credentials.
            </p>
          }
        />
      ) : null}
    </div>
  );
}

function EditConnectionForm({
  row,
  client,
  onCancel,
  onSaved,
}: {
  row: MCPConnection;
  client: CortexClient;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [description, setDescription] = useState(row.description ?? '');
  // Set when the PATCH 409s with CONNECTION_NAME_IN_USE — drives the
  // "Connection is in use" confirm dialog instead of a generic error.
  // (DEV-16944.)
  const [conflict, setConflict] = useState<McpConnectionNameInUseError | null>(null);
  const toast = useToast();

  const patch = useMutation({
    mutationFn: (vars: { propagateRefs: boolean }) =>
      client.http
        .patch(`v1/mcp-connections/${row.id}/metadata`, {
          json: {
            name,
            description: description.trim() ? description.trim() : undefined,
            // Only sent on the confirmed retry — renames the connection AND
            // rewrites every agent / workflow-node reference to it.
            ...(vars.propagateRefs ? { propagateRefs: true } : {}),
          },
        })
        // `propagated` is present only after a propagateRefs retry (and only
        // on servers that support it) — used for the success summary.
        .json<MCPConnection & { propagated?: McpConnectionRenamePropagation }>(),
    onSuccess: (res) => {
      setConflict(null);
      if (res.propagated) {
        toast.show({
          kind: 'success',
          text: `Connection renamed — ${propagatedSummary(res.propagated)}`,
        });
      }
      onSaved();
    },
    onError: (err) => {
      // A structured 409 means "references exist" → confirm dialog. Any
      // other failure falls through to the inline error text below.
      readConnectionInUseBody(err)
        .then((body) => {
          if (body) setConflict(body);
        })
        .catch(() => {});
    },
  });

  return (
    <div
      style={{
        ...stack3,
        background: 'var(--surface-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius)',
        padding: 'var(--sp-4)',
        marginTop: 'var(--sp-2)',
      }}
    >
      <div style={{ ...rowH, justifyContent: 'space-between' }}>
        <p className="t-mono-label" style={{ color: 'var(--text-secondary)' }}>
          EDIT MCP
        </p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          Close
        </button>
      </div>
      <form
        style={stack3}
        onSubmit={(e) => {
          e.preventDefault();
          patch.mutate({ propagateRefs: false });
        }}
      >
        <p className="t-xs t-muted">
          Endpoint, transport, and OAuth settings cannot be changed here — create a new connection
          or use provider tools if you need to change them.
        </p>
        <Field label="Name">
          <input
            required={true}
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label="Description (optional)">
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div style={rowH}>
          <button type="submit" className="btn btn--primary" disabled={patch.isPending}>
            {patch.isPending ? 'Saving…' : 'Save changes'}
          </button>
          <button
            type="button"
            className="btn btn--outline"
            disabled={patch.isPending}
            onClick={onCancel}
          >
            Cancel
          </button>
          {patch.error && !conflict ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {(patch.error as Error).message}
            </span>
          ) : null}
        </div>
      </form>
      <ConfirmDialog
        open={conflict !== null}
        onClose={() => setConflict(null)}
        onConfirm={() => patch.mutate({ propagateRefs: true })}
        title="Connection is in use"
        eyebrow="RENAME MCP CONNECTION"
        message={
          conflict ? <ReferentList referents={conflict.referents} hint={conflict.hint} /> : null
        }
        confirmLabel={`Rename and update ${conflict?.referents.length ?? 0} reference${
          (conflict?.referents.length ?? 0) === 1 ? '' : 's'
        }`}
        pending={patch.isPending}
      />
    </div>
  );
}

function TestResultPanel({
  result,
  pending,
  error,
}: {
  result: ITestConnectionResult | undefined;
  pending: boolean;
  error: unknown;
}) {
  if (pending) return null;
  if (error) {
    return (
      <p className="alert alert--danger" style={{ fontSize: 'var(--fs-xs)' }}>
        Request failed: {(error as Error).message}
      </p>
    );
  }
  if (!result) return null;
  if (!result.ok) {
    return (
      <div className="alert alert--danger" style={{ fontSize: 'var(--fs-xs)' }}>
        <span style={{ fontWeight: 'var(--fw-semi)' }}>{result.error?.code ?? 'ERROR'}:</span>{' '}
        {result.error?.message ?? 'unknown error'}
      </div>
    );
  }
  if (result.tools.length === 0) {
    return (
      <p
        className="t-xs t-muted"
        style={{
          padding: 'var(--sp-2) var(--sp-3)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-sm)',
        }}
      >
        Connected — no tools advertised by this server.
      </p>
    );
  }
  return (
    <div
      style={{
        padding: 'var(--sp-2) var(--sp-3)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-sm)',
        fontSize: 'var(--fs-xs)',
      }}
    >
      <p className="t-muted" style={{ marginBottom: 6 }}>
        {result.tools.length} tool{result.tools.length === 1 ? '' : 's'}:
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {result.tools.map((t) => (
          <span key={t.name} className="chip" title={t.description ?? ''}>
            {t.name}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * The mandatory pre-OAuth warning agreed in the design call: this MCP connection
 * is org-wide, anyone in the org can use or edit it, and any workflow author
 * inherits the credentials. We surface it before the IdP consent screen.
 */
function confirmOrgWideWarning(): boolean {
  return confirm(
    'Heads up: this MCP connection is organization-wide.\n\n' +
      '• Anyone in your org can edit it.\n' +
      '• Anyone in your org can use it in their own workflows.\n' +
      '• If you authenticate with a personal account, others will run workflows as you.\n\n' +
      'For shared use, sign out of the provider and back in with a service account before continuing.',
  );
}

function openOAuthPopup(url: string, onComplete: () => void): void {
  const w = 600;
  const h = 700;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(url, 'cortex-oauth', `width=${w},height=${h},left=${left},top=${top}`);
  if (!popup) {
    alert('Popup blocked — allow popups for this site.');
    return;
  }
  function scheduleRefreshBurst() {
    let remaining = 15;
    onComplete();
    const timer = window.setInterval(() => {
      onComplete();
      remaining -= 1;
      if (remaining <= 0) window.clearInterval(timer);
    }, 1000);
  }
  let finished = false;
  function dispose() {
    window.removeEventListener('message', onMessage);
    window.clearInterval(closedPoller);
    window.clearTimeout(disposeTimer);
  }
  function finish() {
    if (finished) return;
    finished = true;
    dispose();
    // OAuth completion can race with backend persistence; revalidate for a short
    // window so the badge updates without a manual page refresh.
    scheduleRefreshBurst();
  }
  function onMessage(e: MessageEvent) {
    if (
      e.data &&
      typeof e.data === 'object' &&
      (e.data as { source?: string }).source === 'cortex-oauth'
    ) {
      finish();
    }
  }
  window.addEventListener('message', onMessage);
  // IdP redirects can null out window.opener on the callback page, so postMessage
  // never runs — poll closure so the list still refreshes when the user dismisses the popup.
  const closedPoller = window.setInterval(() => {
    if (popup.closed) finish();
  }, 300);
  /** Stop polling if the OAuth window stays open abnormally long (avoid a leaked interval). */
  const disposeTimer = window.setTimeout(() => dispose(), 15 * 60 * 1000);
}

function parseOAuthScopes(raw: string): string[] {
  return raw
    .split(/[\s,]+/u)
    .map((s) => s.trim())
    .filter(Boolean);
}

function validateManualOAuthUrl(label: string, value: string): string | null {
  const t = value.trim();
  if (!t) return `${label} is required for manual OAuth.`;
  try {
    const u = new URL(t);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') {
      return `${label} must be an http(s) URL.`;
    }
  } catch (err: unknown) {
    console.info('console/mcp-connections:validateManualOAuthUrl', err);
    return `${label} must be a valid URL.`;
  }
  return null;
}

/** Client-side validation before POST when OAuth mode is manual. */
function validateManualOAuthFields(fields: {
  authorizationUrl: string;
  tokenUrl: string;
  registrationEndpoint: string;
  clientId: string;
}): string | null {
  const a = validateManualOAuthUrl('Authorization URL', fields.authorizationUrl);
  if (a) return a;
  const b = validateManualOAuthUrl('Token URL', fields.tokenUrl);
  if (b) return b;
  const reg = fields.registrationEndpoint.trim();
  const cid = fields.clientId.trim();
  if (!cid && !reg) {
    return 'Provide a Client ID, or a Registration endpoint for dynamic client registration.';
  }
  if (reg) {
    const r = validateManualOAuthUrl('Registration endpoint', reg);
    if (r) return r;
  }
  return null;
}

function buildOAuthAuthConfigForCreate(
  oauthMode: 'auto' | 'manual',
  auto: { clientId: string; clientSecret: string },
  manual: {
    authorizationUrl: string;
    tokenUrl: string;
    registrationEndpoint: string;
    clientId: string;
    clientSecret: string;
    scopesRaw: string;
  },
): Record<string, unknown> {
  if (oauthMode === 'auto') {
    const id = auto.clientId.trim();
    const secret = auto.clientSecret.trim();
    const cfg: Record<string, unknown> = {};
    if (id) cfg.clientId = id;
    if (secret) cfg.clientSecret = secret;
    return cfg;
  }
  const auth: Record<string, unknown> = {};
  const authz = manual.authorizationUrl.trim();
  const token = manual.tokenUrl.trim();
  const reg = manual.registrationEndpoint.trim();
  const cid = manual.clientId.trim();
  const secret = manual.clientSecret.trim();
  const scopes = parseOAuthScopes(manual.scopesRaw);
  if (authz) auth.authorizationUrl = authz;
  if (token) auth.tokenUrl = token;
  if (reg) auth.registrationEndpoint = reg;
  if (cid) auth.clientId = cid;
  if (secret) auth.clientSecret = secret;
  if (scopes.length) auth.scopes = scopes;
  return auth;
}

function CreateForm({
  client,
  onCreated,
  initial,
  onBack,
}: {
  client: CortexClient;
  onCreated: () => void;
  /** Prefill (used when a template routes into the manual form, e.g. Slack). */
  initial?: CreateFormInitial;
  /** When present, show a "Back to templates" link. */
  onBack?: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState('');
  const [endpoint, setEndpoint] = useState(initial?.endpoint ?? '');
  const [transport, setTransport] = useState<'http' | 'sse'>(initial?.transport ?? 'http');
  /**
   * UI-level auth choice. `none` and `token` both map to the backend
   * `authType: 'none'`; they differ only by whether static credentials are
   * written via `PUT :id/credentials` after the connection is created.
   */
  const [authMode, setAuthMode] = useState<'none' | 'token' | 'oauth'>(initial?.authMode ?? 'none');
  const [tokenAuthorization, setTokenAuthorization] = useState('');
  const [tokenHeaders, setTokenHeaders] = useState<IHeaderPair[]>([]);
  const [oauthMode, setOauthMode] = useState<'auto' | 'manual'>('auto');
  const [oauthClientId, setOauthClientId] = useState('');
  const [oauthClientSecret, setOauthClientSecret] = useState('');
  const [manualAuthorizationUrl, setManualAuthorizationUrl] = useState('');
  const [manualTokenUrl, setManualTokenUrl] = useState('');
  const [manualRegistrationEndpoint, setManualRegistrationEndpoint] = useState('');
  const [manualClientId, setManualClientId] = useState('');
  const [manualClientSecret, setManualClientSecret] = useState('');
  const [manualScopesRaw, setManualScopesRaw] = useState('');
  const [oauthFormError, setOauthFormError] = useState<string | null>(null);

  useEffect(() => {
    if (authMode !== 'oauth') {
      setOauthMode('auto');
      setOauthClientId('');
      setOauthClientSecret('');
      setManualAuthorizationUrl('');
      setManualTokenUrl('');
      setManualRegistrationEndpoint('');
      setManualClientId('');
      setManualClientSecret('');
      setManualScopesRaw('');
      setOauthFormError(null);
    }
    if (authMode !== 'token') {
      setTokenAuthorization('');
      setTokenHeaders([]);
    }
  }, [authMode]);

  const create = useMutation({
    mutationFn: async () => {
      const conn = await client.http
        .post('v1/mcp-connections', {
          json: {
            name,
            description: description || undefined,
            endpoint,
            transport,
            authType: authMode === 'oauth' ? 'oauth' : 'none',
            ...(authMode === 'oauth' && {
              authConfig: buildOAuthAuthConfigForCreate(
                oauthMode,
                { clientId: oauthClientId, clientSecret: oauthClientSecret },
                {
                  authorizationUrl: manualAuthorizationUrl,
                  tokenUrl: manualTokenUrl,
                  registrationEndpoint: manualRegistrationEndpoint,
                  clientId: manualClientId,
                  clientSecret: manualClientSecret,
                  scopesRaw: manualScopesRaw,
                },
              ),
            }),
          },
        })
        .json<MCPConnection>();
      // Static credentials live in Vault, set via a separate call after the
      // connection row exists. authMode 'token' === backend authType 'none'
      // plus a stored keyring.
      if (authMode === 'token') {
        await client.http.put(`v1/mcp-connections/${conn.id}/credentials`, {
          json: {
            authorization: tokenAuthorization.trim(),
            customHeaders: pairsToHeaders(tokenHeaders),
          },
        });
      }
      return conn;
    },
    onSuccess: () => {
      setName('');
      setDescription('');
      setEndpoint('');
      setAuthMode('none');
      setTokenAuthorization('');
      setTokenHeaders([]);
      setOauthMode('auto');
      setOauthClientId('');
      setOauthClientSecret('');
      setManualAuthorizationUrl('');
      setManualTokenUrl('');
      setManualRegistrationEndpoint('');
      setManualClientId('');
      setManualClientSecret('');
      setManualScopesRaw('');
      setOauthFormError(null);
      onCreated();
    },
  });

  return (
    <form
      style={stack3}
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        setOauthFormError(null);
        if (authMode === 'oauth' && oauthMode === 'manual') {
          const err = validateManualOAuthFields({
            authorizationUrl: manualAuthorizationUrl,
            tokenUrl: manualTokenUrl,
            registrationEndpoint: manualRegistrationEndpoint,
            clientId: manualClientId,
          });
          if (err) {
            setOauthFormError(err);
            return;
          }
        }
        if (authMode === 'token' && !tokenAuthorization.trim()) {
          setOauthFormError(
            'Enter an Authorization header value (or a custom header) for API key auth.',
          );
          return;
        }
        create.mutate();
      }}
    >
      {onBack ? (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          style={{ alignSelf: 'flex-start', paddingLeft: 0 }}
          onClick={onBack}
        >
          ← Back to templates
        </button>
      ) : null}
      {initial?.note ? (
        <p
          className="t-xs"
          style={{
            margin: 0,
            padding: 'var(--sp-2) var(--sp-3)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border-subtle)',
            background: 'var(--surface-2)',
            color: 'var(--text-secondary)',
          }}
        >
          {initial.note}
        </p>
      ) : null}
      <div style={grid2}>
        <Field label="Name (unique per org)">
          <input
            required={true}
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="atlassian"
          />
        </Field>
        <Field label="Endpoint URL">
          <input
            required={true}
            className="input"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="https://mcp.atlassian.com/v1/sse"
          />
        </Field>
      </div>
      <Field label="Description (optional)">
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div style={grid2}>
        <Field label="Transport">
          <select
            className="select-native"
            value={transport}
            onChange={(e) => setTransport(e.target.value as 'http' | 'sse')}
          >
            <option value="http">HTTP (streamable)</option>
            <option value="sse">SSE</option>
          </select>
        </Field>
        <Field label="Auth">
          <select
            className="select-native"
            value={authMode}
            onChange={(e) => setAuthMode(e.target.value as 'none' | 'token' | 'oauth')}
          >
            <option value="none">No authentication</option>
            <option value="token">API key / bearer token</option>
            <option value="oauth">OAuth 2.1</option>
          </select>
        </Field>
      </div>
      {authMode === 'token' ? (
        <fieldset
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: 'var(--sp-3)',
            ...stack3,
          }}
        >
          <legend className="t-mono-label" style={{ padding: '0 var(--sp-1)' }}>
            API KEY / TOKEN
          </legend>
          <p className="t-xs t-muted">
            Sent on every request to this server. The token is written to Vault and never returned
            by any read endpoint. Use this for servers that take a static bearer token or PAT (e.g.
            Snowflake).
          </p>
          <Field label="Authorization header value">
            <input
              className="input"
              type="password"
              name="mcp-static-authorization"
              autoComplete="new-password"
              data-1p-ignore={true}
              data-lpignore="true"
              value={tokenAuthorization}
              onChange={(e) => setTokenAuthorization(e.target.value)}
              placeholder="Bearer eyJhbGciOiJ…"
            />
          </Field>
          <Field label="Custom headers (optional)">
            <HeaderPairsEditor pairs={tokenHeaders} onChange={setTokenHeaders} />
          </Field>
        </fieldset>
      ) : null}
      {authMode === 'oauth' ? (
        <fieldset
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            padding: 'var(--sp-3)',
            ...stack3,
          }}
        >
          <legend className="t-mono-label" style={{ padding: '0 var(--sp-1)' }}>
            OAuth
          </legend>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 'var(--sp-4)',
              fontSize: 'var(--fs-xs)',
            }}
          >
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                className="radio"
                name="oauth-mode"
                checked={oauthMode === 'auto'}
                onChange={() => {
                  setOauthMode('auto');
                  setOauthFormError(null);
                }}
              />
              <span>
                <span style={{ fontWeight: 'var(--fw-med)', color: 'var(--text-primary)' }}>
                  Auto
                </span>
                <span className="t-muted"> — discovery from the endpoint URL at Connect</span>
              </span>
              <span
                role="img"
                aria-label="About Auto OAuth"
                title="Use this when the MCP server publishes OAuth metadata (e.g. /.well-known/oauth-authorization-server) reachable from the endpoint above. Optionally set a pre-registered client ID (e.g. Google Cloud) if the provider does not support dynamic registration."
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 15,
                  height: 15,
                  borderRadius: '50%',
                  border: '1px solid var(--border-strong)',
                  color: 'var(--text-muted)',
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: 'help',
                  flexShrink: 0,
                }}
              >
                ?
              </span>
            </label>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                cursor: 'pointer',
              }}
            >
              <input
                type="radio"
                className="radio"
                name="oauth-mode"
                checked={oauthMode === 'manual'}
                onChange={() => {
                  setOauthMode('manual');
                  setOauthFormError(null);
                }}
              />
              <span>
                <span style={{ fontWeight: 'var(--fw-med)', color: 'var(--text-primary)' }}>
                  Advanced / manual OAuth
                </span>
                <span className="t-muted"> — static endpoints (no discovery)</span>
              </span>
            </label>
          </div>
          {oauthMode === 'auto' ? (
            <div style={stack2}>
              <Field label="Client ID (optional)">
                <input
                  className="input"
                  name="mcp-oauth-pre-registered-client-id"
                  autoComplete="off"
                  data-1p-ignore={true}
                  data-lpignore="true"
                  value={oauthClientId}
                  onChange={(e) => setOauthClientId(e.target.value)}
                  placeholder="Leave blank for DCR-only servers"
                />
              </Field>
              <Field label="Client secret (optional)">
                <input
                  type="password"
                  className="input"
                  name="mcp-oauth-auto-client-secret"
                  autoComplete="new-password"
                  data-1p-ignore={true}
                  data-lpignore="true"
                  value={oauthClientSecret}
                  onChange={(e) => setOauthClientSecret(e.target.value)}
                  placeholder="Required for confidential clients (e.g. Slack); omit for public PKCE"
                />
              </Field>
            </div>
          ) : (
            <div style={stack2}>
              <p className="t-xs t-muted">
                {
                  "Paste the provider's OAuth URLs and client registration so Connect does not depend on discovery. Client secret is optional for public PKCE clients; in production you can store secrets in Vault instead of this field."
                }
              </p>
              <div style={grid2}>
                <Field label="Authorization URL">
                  <input
                    required={true}
                    className="input"
                    value={manualAuthorizationUrl}
                    onChange={(e) => setManualAuthorizationUrl(e.target.value)}
                    placeholder="https://…/authorize"
                  />
                </Field>
                <Field label="Token URL">
                  <input
                    required={true}
                    className="input"
                    value={manualTokenUrl}
                    onChange={(e) => setManualTokenUrl(e.target.value)}
                    placeholder="https://…/oauth/token"
                  />
                </Field>
              </div>
              <Field label="Registration endpoint (optional, RFC 7591)">
                <input
                  className="input"
                  value={manualRegistrationEndpoint}
                  onChange={(e) => setManualRegistrationEndpoint(e.target.value)}
                  placeholder="https://…/oauth/register"
                />
              </Field>
              <div style={grid2}>
                <Field label="Client ID">
                  <input
                    className="input"
                    name="mcp-oauth-static-client-id"
                    autoComplete="off"
                    data-1p-ignore={true}
                    data-lpignore="true"
                    value={manualClientId}
                    onChange={(e) => setManualClientId(e.target.value)}
                    placeholder="Required unless using registration only"
                  />
                </Field>
                <Field label="Client secret (optional)">
                  <input
                    type="password"
                    name="mcp-oauth-client-secret"
                    autoComplete="new-password"
                    data-1p-ignore={true}
                    data-lpignore="true"
                    className="input"
                    value={manualClientSecret}
                    onChange={(e) => setManualClientSecret(e.target.value)}
                    placeholder="Omit for public / PKCE-only"
                  />
                </Field>
              </div>
              <Field label="Scopes (comma- or space-separated)">
                <input
                  className="input"
                  value={manualScopesRaw}
                  onChange={(e) => setManualScopesRaw(e.target.value)}
                  placeholder="read:jira-work offline_access"
                />
              </Field>
            </div>
          )}
        </fieldset>
      ) : null}
      <div style={rowHWrap}>
        <button type="submit" className="btn btn--primary" disabled={create.isPending}>
          {create.isPending ? 'Saving…' : 'Save'}
        </button>
        {oauthFormError ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {oauthFormError}
          </span>
        ) : null}
        {create.error ? (
          <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
            {(create.error as Error).message}
          </span>
        ) : null}
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

interface IHeaderPair {
  key: string;
  value: string;
}

/** Collapse the key/value editor rows into the API's `customHeaders` map. */
function pairsToHeaders(pairs: IHeaderPair[]): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  for (const p of pairs) {
    const k = p.key.trim();
    if (k) out[k] = p.value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Human-facing auth label — backend only knows `none` | `oauth`. */
function authLabel(row: MCPConnection): string {
  if (row.authType === 'oauth') return 'OAuth';
  // User-token built-ins authenticate with the connecting user's own Formant
  // login. Their keyring lives on the org-owned row, so the catalog row's
  // `hasKeyring` is always false — labeling it "No auth" would wrongly imply no
  // credentials are needed, when a Connect is required.
  if (row.orgId === null && isUserTokenCat(row.category)) return 'User login';
  return row.hasKeyring ? 'API key' : 'No auth';
}

function HeaderPairsEditor({
  pairs,
  onChange,
}: {
  pairs: IHeaderPair[];
  onChange: (next: IHeaderPair[]) => void;
}) {
  const update = (i: number, field: keyof IHeaderPair, value: string) => {
    onChange(pairs.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  };
  return (
    <div style={stack2}>
      {pairs.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <input
            className="input"
            style={{ flex: 1 }}
            placeholder="Header name"
            value={p.key}
            onChange={(e) => update(i, 'key', e.target.value)}
          />
          <input
            className="input"
            style={{ flex: 2 }}
            placeholder="Value"
            value={p.value}
            onChange={(e) => update(i, 'value', e.target.value)}
          />
          <button
            type="button"
            className="btn btn--outline btn--sm"
            aria-label="Remove header"
            onClick={() => onChange(pairs.filter((_, idx) => idx !== i))}
          >
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        style={{ alignSelf: 'flex-start' }}
        onClick={() => onChange([...pairs, { key: '', value: '' }])}
      >
        + Add header
      </button>
    </div>
  );
}

/**
 * Inline editor for static bearer-token / API-key credentials on an
 * `authType: 'none'` connection. Stored tokens are write-only (never returned
 * by any GET), so the fields always start blank and a save replaces whatever
 * is in Vault.
 */
function CredentialsEditor({
  row,
  client,
  onCancel,
  onSaved,
}: {
  row: MCPConnection;
  client: CortexClient;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [authorization, setAuthorization] = useState('');
  const [headers, setHeaders] = useState<IHeaderPair[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      client.http.put(`v1/mcp-connections/${row.id}/credentials`, {
        json: {
          authorization: authorization.trim(),
          customHeaders: pairsToHeaders(headers),
        },
      }),
    onSuccess: onSaved,
  });
  const clear = useMutation({
    mutationFn: () => client.http.delete(`v1/mcp-connections/${row.id}/credentials`),
    onSuccess: onSaved,
  });

  return (
    <div
      style={{
        ...stack3,
        background: 'var(--surface-2)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius)',
        padding: 'var(--sp-4)',
        marginTop: 'var(--sp-2)',
      }}
    >
      <div style={{ ...rowH, justifyContent: 'space-between' }}>
        <p className="t-mono-label" style={{ color: 'var(--text-secondary)' }}>
          {row.hasKeyring ? 'EDIT CREDENTIALS' : 'ADD CREDENTIALS'}
        </p>
        <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>
          Close
        </button>
      </div>
      <form
        style={stack3}
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          setFormError(null);
          if (!authorization.trim() && !pairsToHeaders(headers)) {
            setFormError('Enter an Authorization header value or at least one custom header.');
            return;
          }
          save.mutate();
        }}
      >
        <p className="t-xs t-muted">
          {row.hasKeyring
            ? 'Credentials are already stored. Saving replaces them; the existing token is never shown.'
            : 'Stored in Vault and sent on every request to this server. The token is never returned by any read endpoint.'}
        </p>
        <Field label="Authorization header value">
          <input
            className="input"
            type="password"
            name="mcp-edit-authorization"
            autoComplete="new-password"
            data-1p-ignore={true}
            data-lpignore="true"
            value={authorization}
            onChange={(e) => setAuthorization(e.target.value)}
            placeholder="Bearer eyJhbGciOiJ…"
          />
        </Field>
        <Field label="Custom headers (optional)">
          <HeaderPairsEditor pairs={headers} onChange={setHeaders} />
        </Field>
        <div style={rowHWrap}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save credentials'}
          </button>
          {row.hasKeyring ? (
            <button
              type="button"
              className="btn btn--outline"
              disabled={clear.isPending}
              onClick={() => {
                if (confirm('Remove stored credentials? Workflows using this server may fail.')) {
                  clear.mutate();
                }
              }}
            >
              {clear.isPending ? 'Clearing…' : 'Clear credentials'}
            </button>
          ) : null}
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
          {formError ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {formError}
            </span>
          ) : null}
          {save.error ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {(save.error as Error).message}
            </span>
          ) : null}
          {clear.error ? (
            <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
              {(clear.error as Error).message}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
